/* eslint-disable @typescript-eslint/no-unused-vars */
import { assign, createMachine } from 'xstate';

import {
  sendDebouncedSortByEvent,
  cancelDebouncedSortByEvent,
} from './actions';

import { defaultSortingParams } from './constants';
import { Context, Events, FiltersSettings } from './types';

import { INITIAL_FILTERS } from '../billingFilters';

const { date, ...restFilters } = INITIAL_FILTERS;

export const initialFilters: FiltersSettings = {
  ...restFilters,
  dateField: null,
  startDate: null,
  endDate: null,
};

export const initialBillingHistoryContext: Context = {
  sorting: {
    page: null,
    limit: null,
    ...defaultSortingParams,
  },
  filters: initialFilters,
  pagination: null,
  items: [],
};

const billingHistoryData = createMachine<Context, Events>({
  id: 'billingHistoryData',
  predictableActionArguments: true,
  context: initialBillingHistoryContext,
  type: 'parallel',
  states: {
    machineStatus: {
      initial: 'idle',
      on: {
        INITIALIZE: {
          target: '.initialized',
        },
      },
      states: {
        idle: {},
        initialized: {},
      },
    },
    apiClient: {
      initial: 'idle',
      on: {
        INITIALIZE: {
          target: '.loading',
          actions: assign((ctx, event) => ({
            sorting: {
              ...ctx.sorting,
              page: event.page,
              limit: event.limit,
            },
          })),
        },
        DEBOUNCED_SORT_BY: {
          target: '.loading',
        },
        CHANGE_FILTERS: {
          target: '.loading',
          actions: assign((_ctx, event) => {
            return {
              filters: {
                ...event.filters,
              },
            };
          }),
        },
        CLEAR_FILTERS: {
          target: '.loading',
          actions: assign((ctx) => {
            return {
              ...ctx,
              filters: initialFilters,
            };
          }),
        },
        CHANGE_PAGE: {
          target: '.loading',
          actions: [
            assign((ctx, event) => {
              return {
                sorting: {
                  ...ctx.sorting,
                  page: event.page,
                },
              };
            }),
          ],
        },
        CHANGE_ITEMS_PER_PAGE: {
          target: '.loading',
          actions: [
            assign((ctx, event) => {
              return {
                sorting: {
                  ...ctx.sorting,
                  page: 1,
                  limit: event.itemsPerPage,
                  pagination: {
                    ...ctx.pagination,
                    itemsPerPage: event.itemsPerPage,
                  },
                },
              };
            }),
          ],
        },
        LOAD_MORE: {
          target: '.loading',
          actions: assign((ctx, event) => {
            return {
              sorting: {
                ...ctx.sorting,
                limit: ctx.sorting.limit + event.limit,
              },
            };
          }),
        },
      },
      states: {
        idle: {},
        error: {
          on: {
            INITIALIZE: {
              target: 'loading',
            },
          },
        },
        loading: {
          invoke: {
            src: 'getInvoices',
            onDone: {
              target: 'idle',
              actions: assign((_ctx, event) => {
                return {
                  items: event.data.items,
                  pagination: event.data.pagination,
                };
              }),
            },
            onError: {
              target: 'error',
            },
          },
        },
      },
    },
    sorting: {
      initial: 'idle',
      states: {
        idle: {
          on: {
            SORT_BY: {
              actions: [
                assign((ctx, event) => {
                  return {
                    sorting: {
                      ...ctx.sorting,
                      sortDirection: event.sortDirection,
                      sortBy: event.sortBy,
                    },
                  };
                }),
                cancelDebouncedSortByEvent,
                sendDebouncedSortByEvent,
              ],
            },
          },
        },
      },
    },
  },
});

export { billingHistoryData };
